import classNames from 'classnames';

import {CourseBadgeFamily, SubsDiff} from '@udemy-v2/react-plans-differentiation';
import {
    aggregateCollectionStats,
    FALLBACK_COLLECTION_STATS,
    SubscriptionOfferImpressionEvent,
} from '@udemy-v2/react-subscription-browse';
import {tokens} from '@udemy-v2/styles';
import {TrackImpression, publishEvent} from '@udemy-v2/event-tracking';
import {useFeatures} from '@udemy-v2/react-experimentation';
import {useMatchMedia} from '@udemy-v2/design-system-utils';
import {useCommonData} from '@udemy-v2/common-data';

import {CourseIncentiveImpressionEvent, IncentiveType} from '../../events/events';
import {useClpCourse} from '../../context/clp-course-provider.react-component';
import {useClpPurchaseContext} from '../../context/clp-purchase-context.react-component';
import {useClpContext} from '../../context/clp-context-provider.react-component';

import styles from './use-subs-diff.module.scss';

export interface SubsDiffResult {
    shouldAddSubsDiffSpacing: boolean;
    shouldShowSubsDiff: boolean;
    isSubsDiffLoading: boolean;
    SubsDiffComponent: React.FC;
}

export const useSubsDiff = (): SubsDiffResult => {
    const isDesktop = useMatchMedia(`(min-width: ${tokens['viewport-lg-min']})`);
    const course = useClpCourse();
    const commonData = useCommonData();
    const hasOrganization = !!commonData?.organization;
    const {
        isCourseInAvailablePlans,
        isCourseIncludedInUserSubscription,
        unwrappedConsumerSubscriptionPlan,
    } = useClpPurchaseContext();
    const contentCollections = unwrappedConsumerSubscriptionPlan?.contentCollections;
    const subscriptionPlanStats = contentCollections
        ? aggregateCollectionStats(
              contentCollections.map((contentCollection) => contentCollection.stats),
          )
        : FALLBACK_COLLECTION_STATS;
    const {isLoading: isLoadingFeatures, featureVariantAssignments} = useFeatures([
        'subscription_catalog_differentiator_feature',
    ]);
    const featureConfig = featureVariantAssignments.get(
        'subscription_catalog_differentiator_feature',
    )?.configuration;

    const hasBadgeFeatureEnabled = Boolean(
        featureConfig?.showTopPickBadge ||
            featureConfig?.showIncludedInPlansBadge ||
            featureConfig?.showPremiumBadge,
    );

    const handleBadgePopoverView = () => {
        if (unwrappedConsumerSubscriptionPlan) {
            publishEvent(
                new SubscriptionOfferImpressionEvent({
                    badges: null,
                    courseId: Number(course.id),
                    courseTrackingId: String(course.id),
                    hasTrial: Boolean(unwrappedConsumerSubscriptionPlan.trialPeriodDays),
                    planId: unwrappedConsumerSubscriptionPlan.id,
                    planPriceIds: unwrappedConsumerSubscriptionPlan.planPriceOptionIds,
                    trialDays: unwrappedConsumerSubscriptionPlan.trialPeriodDays,
                    uiRegion: 'body',
                }),
            );
        }
    };

    const shouldShowSubsDiff =
        hasBadgeFeatureEnabled &&
        isCourseInAvailablePlans &&
        !hasOrganization &&
        !isCourseIncludedInUserSubscription;

    const badgeFamily = featureConfig?.showPremiumBadge
        ? CourseBadgeFamily.PREMIUM
        : featureConfig?.showIncludedInPlansBadge
          ? CourseBadgeFamily.INCLUDED_IN_PLANS
          : CourseBadgeFamily.TOP_PICK;

    const SubsDiffComponent: React.FC = () => {
        const {openReviewsModal} = useClpContext();

        if (!shouldShowSubsDiff) return null;

        return (
            <div className={classNames('ud-color-scheme-light', styles['subs-diff-wrapper'])}>
                <TrackImpression
                    trackFunc={() =>
                        publishEvent(
                            new CourseIncentiveImpressionEvent({
                                courseId: Number(course.id),
                                courseTrackingId: String(course.id),
                                incentiveType: IncentiveType.SUBSCRIPTION_DIFFERENTIATION,
                                incentiveValue: '',
                                position: 0,
                            }),
                        )
                    }
                >
                    <div className={styles['subs-diff-container']}>
                        <SubsDiff
                            badgeFamily={badgeFamily}
                            numLearners={course.enrollments?.count || 0}
                            numRatings={course.rating?.count || 0}
                            rating={course.rating?.average || 0}
                            isLoadingCollectionStats={isLoadingFeatures}
                            numCourses={subscriptionPlanStats.numberOfCourses}
                            onRatingsClick={openReviewsModal}
                            onBadgePopoverView={handleBadgePopoverView}
                        />
                    </div>
                </TrackImpression>
            </div>
        );
    };

    return {
        shouldAddSubsDiffSpacing: Boolean(isDesktop) && shouldShowSubsDiff,
        shouldShowSubsDiff,
        isSubsDiffLoading: !!(isLoadingFeatures || unwrappedConsumerSubscriptionPlan?.isLoading),
        SubsDiffComponent,
    };
};
